import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import IProjectGroupCompletionItem from "../dataTypes/IProjectGroupCompletionItem";

// Pre-defined icons for better performance
const CompletedIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-3 w-3 text-white"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
  </svg>
);

const InProgressIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-3.5 w-3.5 text-white"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <circle cx="12" cy="12" r="9" strokeWidth={2} />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 7v5l3 3" />
  </svg>
);

// Status configuration without JSX
const STATUS_CONFIG = {
  completed: { bgColor: "bg-green-500", borderColor: "border-green-500" },
  inProgress: { bgColor: "bg-yellow-500", borderColor: "border-yellow-500" },
  notStarted: { bgColor: "bg-gray-300", borderColor: "border-gray-300" },
} as const;

interface EnduserModuleFunnelChartProps {
  modules: IProjectGroupCompletionItem[];
}

const EnduserModuleFunnelChart = memo(({ modules }: EnduserModuleFunnelChartProps) => {
  const { t } = useTranslation();

  // Sort modules by order and calculate status using API fields
  const sortedModules = useMemo(() => {
    return [...modules]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((module, index) => {
        // Derive status from API fields
        const isCompleted = module.progressPercentage === 100 || module.isPerfectScore === true;
        const hasStarted =
          !!module.dateLastSession ||
          (module.progressPercentage !== null && module.progressPercentage > 0);

        let status: "completed" | "inProgress" | "notStarted" = "notStarted";
        if (isCompleted) {
          status = "completed";
        } else if (hasStarted) {
          status = "inProgress";
        }

        return {
          id: module.projectId,
          name: module.projectName,
          status,
          progress: module.progressPercentage ?? 0,
          order: module.order ?? 0,
          displayIndex: index + 1,
        };
      });
  }, [modules]);

  if (sortedModules.length === 0) {
    return (
      <div className="flex items-center justify-center h-24 text-gray-500 text-sm">
        {t("statsNew.charts.noData")}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 py-2 overflow-visible">
      {sortedModules.map((module, index) => {
        const config = STATUS_CONFIG[module.status];
        const isLast = index === sortedModules.length - 1;

        return (
          <div key={module.id} className="flex items-center flex-shrink-0">
            {/* Module circle */}
            <div className="relative group">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center ${config.bgColor} border-2 ${config.borderColor}`}
              >
                {module.status === "completed" ? (
                  <CompletedIcon />
                ) : module.status === "inProgress" ? (
                  <InProgressIcon />
                ) : (
                  <span className="text-[8px] font-medium text-gray-500">
                    {module.displayIndex}
                  </span>
                )}
              </div>

              {/* Tooltip - aligned left to avoid sidebar crop */}
              <div className="absolute top-full left-0 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                {module.name}
                {module.progress > 0 && (
                  <span className="text-gray-300"> ({module.progress}%)</span>
                )}
              </div>
            </div>

            {/* Connector line */}
            {!isLast && (
              <div
                className={`w-6 h-0.5 ${
                  module.status === "completed" ? "bg-green-500" : "bg-gray-300"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
});

EnduserModuleFunnelChart.displayName = "EnduserModuleFunnelChart";

export default EnduserModuleFunnelChart;
